import type { CSSProperties } from 'react'
import type { Order } from '@/types'
import type { OrderTypeConfig } from '@/lib/orderTypeSettings'
import { colorForOrderTypeId, sanitizeOrderTypeHexColor } from '@/lib/orderTypeSettings'
import { cn } from '@/lib/utils'

export type TicketLifecycle =
  | 'empty'
  | 'open_tab'
  | 'staging'
  | 'fired'
  | 'adding'
  | 'ready_to_pay'
  | 'partial_paid'
  | 'closed'

export interface TicketLifecycleInput {
  order: Order | null | undefined
  cartCount: number
  checkoutOpen: boolean
  payableRemaining: number
}

export interface TicketLifecycleMeta {
  /** Machine value */
  state: TicketLifecycle
  /** Short label shown as pill (e.g. "Open tab", "Adding items") */
  label: string
  /** One-line sub-label under the pill */
  helper: string
  /** Visual tone for the pill */
  tone: 'neutral' | 'amber' | 'emerald' | 'violet' | 'sky' | 'slate'
}

/** Count non-voided items sent to the kitchen. */
export function countFiredItems(order: Order | null | undefined): number {
  if (!order?.items?.length) return 0
  return order.items.filter((i) => i.status !== 'voided' && i.status !== 'draft').length
}

/** Central lifecycle resolver for the right rail ticket. */
export function getTicketLifecycle(input: TicketLifecycleInput): TicketLifecycleMeta {
  const { order, cartCount, checkoutOpen, payableRemaining } = input
  const fired = countFiredItems(order)
  const hasCart = cartCount > 0

  if (checkoutOpen && order) {
    const anyPaid = (order.payments ?? []).some((p) => p.status === 'completed')
    if (payableRemaining <= 0) {
      // A Rs 0 ticket with no tender recorded is still open server-side — it
      // needs "Complete order — Rs 0". Calling it "Fully paid" left KOF's
      // zero-priced 20 Sep tickets looking settled while they sat on the strip
      // (audit/KOF_ZERO_PRICE_OPEN_ORDERS_2026-09-25.md).
      if (order.status !== 'completed' && !anyPaid) {
        return {
          state: 'ready_to_pay',
          label: 'Ready to close',
          helper: 'Nothing to charge — complete the order to close it',
          tone: 'violet',
        }
      }
      return {
        state: 'closed',
        label: 'Closed',
        helper: 'Fully paid',
        tone: 'slate',
      }
    }
    if (anyPaid) {
      return {
        state: 'partial_paid',
        label: 'Partial',
        helper: 'Awaiting remaining balance',
        tone: 'violet',
      }
    }
    return {
      state: 'ready_to_pay',
      label: 'Ready to pay',
      helper: 'Choose tender and complete payment',
      tone: 'violet',
    }
  }

  if (!order && !hasCart) {
    return {
      state: 'empty',
      label: 'Empty',
      helper: 'Pick a table or order type to start',
      tone: 'neutral',
    }
  }

  if (order && fired === 0 && !hasCart) {
    return {
      state: 'open_tab',
      label: 'Open tab',
      helper: 'Order reserved — add items to fire',
      tone: 'amber',
    }
  }

  if (order && fired > 0 && hasCart) {
    return {
      state: 'adding',
      label: 'Adding items',
      helper: 'New items will fire on Send',
      tone: 'sky',
    }
  }

  if (!order && hasCart) {
    return {
      state: 'staging',
      label: 'Staging',
      helper: 'Items not yet sent to the kitchen',
      tone: 'sky',
    }
  }

  if (order && fired > 0 && !hasCart) {
    return {
      state: 'fired',
      label: 'Fired',
      helper: 'All items on check',
      tone: 'emerald',
    }
  }

  return {
    state: 'staging',
    label: 'Staging',
    helper: 'Items not yet sent to the kitchen',
    tone: 'sky',
  }
}

export function orderPayableRemaining(order: Order | null | undefined): number {
  if (!order) return 0
  if (order.status === 'completed' || order.status === 'cancelled') return 0
  const paid =
    order.payments?.filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0
  return Math.max(0, order.total_amount - paid)
}

/**
 * Non-terminal statuses that belong on the counter "Open orders" strip.
 * Must stay in sync with the backend `orders_status_check` non-terminal set
 * (everything except 'completed'/'cancelled') so nothing the `open_only` fetch
 * returns is silently filtered back out. `out_for_delivery` (online COD
 * deliveries with the rider) still needs closing on return — the owner rule is
 * "open must always show" (audit/OPEN_ORDERS_STRIP_HIDDEN_2026-07-23.md).
 */
export const OPEN_ORDERS_STRIP_STATUSES = new Set([
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'out_for_delivery',
  'served',
])

/**
 * Strip membership for Open Orders. Open = visible, including Rs 0 seated
 * tabs. Do not gate on payable balance — that hid occupied tables from the
 * cashier (audit/OPEN_ORDERS_STRIP_HIDDEN_2026-07-23.md).
 */
export function isOpenOrdersStripMember(
  order: { status?: string } | null | undefined,
): boolean {
  if (!order?.status) return false
  return OPEN_ORDERS_STRIP_STATUSES.has(order.status)
}

/**
 * Build the Open Orders strip for the active order type.
 *
 * - Always filters to non-terminal members of `openOrders` (caller should
 *   fetch with open_only=true so completed takeaways cannot page them out).
 * - For dine_in, also unions every table.current_order so the strip stays
 *   1:1 with the Tables picker's occupied set even if the list endpoint
 *   momentarily lags (OPEN_ORDERS_STRIP_HIDDEN_2026-07-23 P1).
 */
export function buildOpenOrdersStrip(
  openOrders: Order[],
  tables: Array<{
    current_order?: {
      id: string
      order_number: string
      customer_name?: string
      status: string
      created_at: string
      total_amount: number
      guest_count: number
      is_open_tab?: boolean
    } | null
  }>,
  orderType: string,
): Order[] {
  const byId = new Map<string, Order>()
  for (const o of openOrders) {
    if (o.order_type !== orderType) continue
    if (!isOpenOrdersStripMember(o)) continue
    byId.set(o.id, o)
  }

  if (orderType === 'dine_in') {
    for (const t of tables) {
      const co = t.current_order
      if (!co?.id || byId.has(co.id)) continue
      if (!isOpenOrdersStripMember(co)) continue
      byId.set(co.id, {
        id: co.id,
        order_number: co.order_number,
        order_type: 'dine_in',
        status: co.status as Order['status'],
        customer_name: co.customer_name,
        total_amount: co.total_amount,
        guest_count: co.guest_count,
        is_open_tab: co.is_open_tab ?? false,
        subtotal: co.total_amount,
        tax_amount: 0,
        discount_amount: 0,
        service_charge_amount: 0,
        delivery_fee_amount: 0,
        created_at: co.created_at,
        updated_at: co.created_at,
      } as Order)
    }
  }

  return [...byId.values()].sort((a, b) => {
    const ta = Date.parse(a.created_at) || 0
    const tb = Date.parse(b.created_at) || 0
    return tb - ta
  })
}

/**
 * The identity an Open Orders tile leads with: which table, or whose order.
 *
 * The tile used to lead with the order number, which is the one thing nobody
 * scans for — an operator hunting a ticket is looking for "table 7" or
 * "Ayesha", and had to open the table picker to find either. This resolves the
 * label that replaces it.
 *
 * Dine-in resolves against the tables list two ways, because the strip is
 * built from two sources (buildOpenOrdersStrip unions the orders list with
 * table.current_order) and only one of them carries `table_id`.
 *
 * Returns '' when neither a table nor a name is known. Callers render nothing
 * in that case — an empty slot reads faster than a placeholder dash.
 */
export function openOrderTagLabel(
  order: { id: string; table_id?: string; customer_name?: string },
  tables: Array<{
    id?: string
    table_number?: string
    current_order?: { id: string } | null
  }>,
): string {
  const match =
    (order.table_id ? tables.find((t) => t.id && t.id === order.table_id) : undefined) ??
    tables.find((t) => t.current_order?.id === order.id)

  const num = match?.table_number?.trim()
  if (num) {
    // Tables are numbered inconsistently per tenant — KIM MUN runs both "T01"
    // and a bare "7". A lone digit in a tile reads as a quantity, so bare
    // numerics get the T; anything already labelled is left exactly as printed.
    return /^\d+$/.test(num) ? `T${num}` : num.slice(0, 10)
  }

  // Takeaway / delivery have no table, so the guest is the identity. First name
  // only: it is what staff say out loud, and the tile has room for one word.
  const first = (order.customer_name ?? '').trim().split(/\s+/)[0] ?? ''
  return first.slice(0, 10)
}

const ORDER_TYPE_PILL_BASE =
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide shadow-sm'

/** Order-type pill — tint from settings colors (matches checkout toggle). */
export function orderTypePillLook(orderTypeId: string, rows: OrderTypeConfig[]): {
  className: string
  style: CSSProperties
} {
  const hex = colorForOrderTypeId(rows, orderTypeId) ?? '#64748b'
  const h = sanitizeOrderTypeHexColor(hex, '#64748b')
  return {
    className: ORDER_TYPE_PILL_BASE,
    style: {
      borderColor: `color-mix(in srgb, ${h} 42%, hsl(var(--border)))`,
      backgroundColor: `color-mix(in srgb, ${h} 20%, hsl(var(--muted) / 0.35))`,
    },
  }
}

/** Tailwind classes for the lifecycle pill by tone. */
export function ticketPillClasses(tone: TicketLifecycleMeta['tone']): string {
  switch (tone) {
    case 'amber':
      return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-100'
    case 'emerald':
      return 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100'
    case 'violet':
      return 'border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-100'
    case 'sky':
      return 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-100'
    case 'slate':
      return 'border-input bg-muted/50 text-foreground'
    case 'neutral':
    default:
      return 'border-border bg-muted/40 text-foreground'
  }
}

/**
 * Lifecycle pill — when tone is neutral, tint lightly with the active order type accent.
 */
export function ticketLifecyclePillLook(
  tone: TicketLifecycleMeta['tone'],
  orderTypeId: string,
  rows: OrderTypeConfig[]
): { className: string; style?: CSSProperties } {
  if (tone !== 'neutral') {
    return { className: cn(ORDER_TYPE_PILL_BASE, ticketPillClasses(tone)) }
  }
  const hex = colorForOrderTypeId(rows, orderTypeId) ?? '#64748b'
  const h = sanitizeOrderTypeHexColor(hex, '#64748b')
  return {
    className: ORDER_TYPE_PILL_BASE,
    style: {
      borderColor: `color-mix(in srgb, ${h} 34%, hsl(var(--border)))`,
      backgroundColor: `color-mix(in srgb, ${h} 11%, hsl(var(--muted) / 0.25))`,
    },
  }
}
